<template>
  <div class="order-analytics-page page-wrapper">
    <h2 class="page-h1">Order Analytics & Forecasting</h2>
    
    <div class="text-center" v-if="loading">
      <circle-loader :size="40"></circle-loader>
    </div>

    <div v-if="!loading && analytics">
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="summary-card">
            <h5>This Year</h5>
            <div class="big-number">{{ analytics.yearOverYear.thisYear.count }}</div>
            <div class="sub-text">
              ${{ formatCurrency(analytics.yearOverYear.thisYear.value) }}
            </div>
            <div
              class="trend"
              :class="yearOverYearTrend > 0 ? 'positive' : 'negative'"
            >
              {{ yearOverYearTrend > 0 ? '+' : '' }}{{ yearOverYearTrend }}% vs Last Year
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <h5>Last Year (Same Period)</h5>
            <div class="big-number">{{ analytics.samePeriodLastYear.count }}</div>
            <div class="sub-text">
              ${{ formatCurrency(analytics.samePeriodLastYear.totalValue) }}
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <h5>Avg Order Value</h5>
            <div class="big-number" v-if="analytics.avgOrderValue.length">
              ${{ formatCurrency(avgOrderValueTotal) }}
            </div>
            <div class="sub-text" v-if="analytics.avgOrderValue.length">
              Across {{ totalOrdersWithValue }} orders
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <h5>In Manufacturing</h5>
            <div class="big-number">{{ manufacturingOrderCount }}</div>
            <div class="sub-text">
              ${{ formatCurrency(manufacturingOrderValue) }}
            </div>
            <div class="sub-text-small">Orders needing inventory</div>
          </div>
        </div>
      </div>

      <!-- Orders by Type and Status -->
      <div class="row mb-4">
        <div class="col-md-6">
          <div class="chart-card">
            <div class="chart-header">
              <h4>Orders by Type</h4>
              <div class="toggle-group">
                <button
                  :class="{ active: !showTypeValue }"
                  @click="showTypeValue = false"
                >
                  Count
                </button>
                <button
                  :class="{ active: showTypeValue }"
                  @click="showTypeValue = true"
                >
                  Value
                </button>
              </div>
            </div>
            <orders-by-type-chart
              :data="analytics.ordersByType"
              :show-value="showTypeValue"
            ></orders-by-type-chart>
          </div>
        </div>
        <div class="col-md-6">
          <div class="chart-card">
            <h4>Orders by Status</h4>
            <orders-by-status-chart
              :data="analytics.ordersByStatus"
            ></orders-by-status-chart>
          </div>
        </div>
      </div>

      <!-- Time-based Analysis -->
      <div class="section-header">
        <h3>Time-Based Analysis</h3>
        <p>Track order trends over time to forecast demand and plan inventory</p>
      </div>

      <div class="row mb-4">
        <div class="col-12">
          <div class="chart-card">
            <div class="chart-header">
              <h4>Orders by Month (Last 12 Months)</h4>
              <div class="toggle-group">
                <button
                  :class="{ active: !showMonthValue }"
                  @click="showMonthValue = false"
                >
                  Count
                </button>
                <button
                  :class="{ active: showMonthValue }"
                  @click="showMonthValue = true"
                >
                  Value
                </button>
              </div>
            </div>
            <orders-by-month-chart
              :data="analytics.ordersByMonth"
              :show-value="showMonthValue"
            ></orders-by-month-chart>
          </div>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-12">
          <div class="chart-card">
            <div class="chart-header">
              <h4>Quarterly Performance</h4>
              <div class="toggle-group">
                <button
                  :class="{ active: !showQuarterValue }"
                  @click="showQuarterValue = false"
                >
                  Count
                </button>
                <button
                  :class="{ active: showQuarterValue }"
                  @click="showQuarterValue = true"
                >
                  Value
                </button>
              </div>
            </div>
            <orders-by-quarter-chart
              :data="analytics.ordersByQuarter"
              :show-value="showQuarterValue"
            ></orders-by-quarter-chart>
          </div>
        </div>
      </div>

      <!-- Upcoming Work -->
      <div class="section-header">
        <h3>Active Pipeline</h3>
        <p>Current orders in progress by workflow stage</p>
      </div>

      <div class="row mb-4">
        <div class="col-md-6">
          <div class="chart-card">
            <h4>Orders by Workflow Stage</h4>
            <upcoming-orders-by-stage-chart
              :data="analytics.upcomingByStage"
            ></upcoming-orders-by-stage-chart>
          </div>
        </div>
        <div class="col-md-6">
          <div class="chart-card">
            <h4>Manufacturing Pipeline</h4>
            <div v-if="analytics.manufacturingPipeline.length === 0">
              <p class="text-center text-muted mt-5">
                No orders currently in manufacturing
              </p>
            </div>
            <div v-else class="pipeline-list">
              <div
                v-for="stage in analytics.manufacturingPipeline"
                :key="stage._id"
                class="pipeline-item"
              >
                <div class="stage-name">{{ stage._id }}</div>
                <div class="stage-count">{{ stage.count }} orders</div>
                <div class="stage-value">
                  ${{ formatCurrency(stage.totalValue) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sales Performance -->
      <div class="section-header">
        <h3>Sales Performance</h3>
        <p>Top performing sales team members</p>
      </div>

      <div class="row mb-4">
        <div class="col-12">
          <div class="chart-card">
            <div class="chart-header">
              <h4>Top 10 Salespeople</h4>
              <div class="toggle-group">
                <button
                  :class="{ active: !showSalesValue }"
                  @click="showSalesValue = false"
                >
                  Count
                </button>
                <button
                  :class="{ active: showSalesValue }"
                  @click="showSalesValue = true"
                >
                  Value
                </button>
              </div>
            </div>
            <orders-by-salesperson-chart
              v-if="analytics.ordersBySalesperson.length"
              :data="analytics.ordersBySalesperson"
              :show-value="showSalesValue"
            ></orders-by-salesperson-chart>
            <p v-else class="text-center text-muted mt-5">
              No sales data available
            </p>
          </div>
        </div>
      </div>

      <!-- Inventory Analysis -->
      <div class="section-header">
        <h3>Inventory Forecast & Analysis</h3>
        <p>
          Bill of Materials summary for building orders - tubes, sheets, and
          hardware needed
        </p>
      </div>

      <div class="row mb-4" v-if="analytics.inventory">
        <div class="col-md-6">
          <div class="chart-card">
            <h4>
              Upcoming Inventory Needs
              <span class="badge">{{ upcomingOrderCount }} Building Orders</span>
            </h4>
            <div
              v-if="!upcomingInventory || upcomingOrderCount === 0"
              class="text-center text-muted mt-5"
            >
              <p>No building orders currently in manufacturing</p>
            </div>
            <div v-else>
              <h5 class="mt-4 mb-3">Tubing Required</h5>
              <inventory-tubes-chart
                :data="upcomingInventory"
              ></inventory-tubes-chart>
              <h5 class="mt-4 mb-3">Sheet Metal Required</h5>
              <inventory-sheets-chart
                :data="upcomingInventory"
              ></inventory-sheets-chart>
              <div class="inventory-details mt-4">
                <h5>Hardware & Other Items</h5>
                <div class="row">
                  <div class="col-6">
                    <div class="detail-item">
                      <strong>Openings:</strong>
                      {{ upcomingInventory.openings.manDoors }} Man Doors,
                      {{ upcomingInventory.openings.rollDoors }} Roll Doors,
                      {{ upcomingInventory.openings.windows }} Windows
                    </div>
                    <div class="detail-item">
                      <strong>Closures:</strong>
                      {{ upcomingInventory.hardware.closures }}
                    </div>
                    <div class="detail-item">
                      <strong>Brackets:</strong>
                      {{ upcomingInventory.hardware.cornerBrackets }} Corner,
                      {{ upcomingInventory.hardware.twoInchBrackets }} 2",
                      {{ upcomingInventory.hardware.fiveInchBrackets }} 5"
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="detail-item">
                      <strong>Screws:</strong>
                      {{ formatNumber(upcomingInventory.screws.sheet) }} Sheet,
                      {{ formatNumber(upcomingInventory.screws.stitch) }} Stitch
                    </div>
                    <div class="detail-item">
                      <strong>Trim:</strong>
                      {{ upcomingInventory.hardware.ridgeCaps }} Ridge Caps,
                      {{ upcomingInventory.hardware.jTrim }} J-Trim
                    </div>
                    <div class="detail-item">
                      <strong>Titan Bolts:</strong>
                      {{ upcomingInventory.hardware.titanBolts }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="chart-card">
            <h4>
              Historical Inventory Used (Last 12 Months)
              <span class="badge">{{ historicalOrderCount }} Building Orders</span>
            </h4>
            <div v-if="!historicalInventory">
              <p class="text-center text-muted mt-5">No historical data</p>
            </div>
            <div v-else>
              <h5 class="mt-4 mb-3">Tubing Used</h5>
              <inventory-tubes-chart
                :data="historicalInventory"
              ></inventory-tubes-chart>
              <h5 class="mt-4 mb-3">Sheet Metal Used</h5>
              <inventory-sheets-chart
                :data="historicalInventory"
              ></inventory-sheets-chart>
              <div class="inventory-details mt-4">
                <h5>Hardware & Other Items</h5>
                <div class="row">
                  <div class="col-6">
                    <div class="detail-item">
                      <strong>Openings:</strong>
                      {{ historicalInventory.openings.manDoors }} Man Doors,
                      {{ historicalInventory.openings.rollDoors }} Roll Doors,
                      {{ historicalInventory.openings.windows }} Windows
                    </div>
                    <div class="detail-item">
                      <strong>Closures:</strong>
                      {{ historicalInventory.hardware.closures }}
                    </div>
                    <div class="detail-item">
                      <strong>Brackets:</strong>
                      {{ historicalInventory.hardware.cornerBrackets }} Corner,
                      {{ historicalInventory.hardware.twoInchBrackets }} 2",
                      {{ historicalInventory.hardware.fiveInchBrackets }} 5"
                    </div>
                  </div>
                  <div class="col-6">
                    <div class="detail-item">
                      <strong>Screws:</strong>
                      {{ formatNumber(historicalInventory.screws.sheet) }} Sheet,
                      {{ formatNumber(historicalInventory.screws.stitch) }} Stitch
                    </div>
                    <div class="detail-item">
                      <strong>Trim:</strong>
                      {{ historicalInventory.hardware.ridgeCaps }} Ridge Caps,
                      {{ historicalInventory.hardware.jTrim }} J-Trim
                    </div>
                    <div class="detail-item">
                      <strong>Titan Bolts:</strong>
                      {{ historicalInventory.hardware.titanBolts }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Inventory Trends by Month -->
      <div class="row mb-4" v-if="analytics.inventory && analytics.inventory.byMonth">
        <div class="col-12">
          <div class="chart-card">
            <div class="chart-header">
              <h4>Monthly Inventory Trends</h4>
              <div class="toggle-group">
                <button
                  :class="{ active: inventoryTrendType === 'tubes' }"
                  @click="inventoryTrendType = 'tubes'"
                >
                  Tubing
                </button>
                <button
                  :class="{ active: inventoryTrendType === 'sheets' }"
                  @click="inventoryTrendType = 'sheets'"
                >
                  Sheets
                </button>
              </div>
            </div>
            <inventory-by-month-chart
              :data="analytics.inventory.byMonth"
              :inventory-type="inventoryTrendType"
            ></inventory-by-month-chart>
          </div>
        </div>
      </div>

      <!-- Average Order Values -->
      <div class="section-header">
        <h3>Order Value Analysis</h3>
      </div>

      <div class="row mb-4">
        <div class="col-12">
          <div class="chart-card">
            <h4>Average Order Value by Type</h4>
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>Order Type</th>
                    <th>Count</th>
                    <th>Total Value</th>
                    <th>Average Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="type in analytics.avgOrderValue"
                    :key="type._id"
                  >
                    <td><strong>{{ type._id || 'Unknown' }}</strong></td>
                    <td>{{ type.count }}</td>
                    <td>${{ formatCurrency(type.totalValue) }}</td>
                    <td>${{ formatCurrency(type.avgValue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Time Metrics -->
      <div class="section-header" v-if="analytics.timeMetrics.length">
        <h3>Completion Time Analysis</h3>
        <p>Average days from sale to completion</p>
      </div>

      <div class="row mb-4" v-if="analytics.timeMetrics.length">
        <div class="col-12">
          <div class="chart-card">
            <h4>Average Days to Complete by Order Type</h4>
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>Order Type</th>
                    <th>Sample Size</th>
                    <th>Avg Days to Complete</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="metric in analytics.timeMetrics"
                    :key="metric._id"
                  >
                    <td><strong>{{ metric._id || 'Unknown' }}</strong></td>
                    <td>{{ metric.count }} orders</td>
                    <td>{{ Math.round(metric.avgDaysToComplete) }} days</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import { userStore } from '@/stores/userStore'
import CircleLoader from '@/components/CircleLoader.vue'
import OrdersByMonthChart from '@/components/OrdersByMonthChart'
import OrdersByQuarterChart from '@/components/OrdersByQuarterChart'
import OrdersByTypeChart from '@/components/OrdersByTypeChart'
import OrdersByStatusChart from '@/components/OrdersByStatusChart'
import UpcomingOrdersByStageChart from '@/components/UpcomingOrdersByStageChart'
import OrdersBySalespersonChart from '@/components/OrdersBySalespersonChart'
import InventoryTubesChart from '@/components/InventoryTubesChart'
import InventorySheetsChart from '@/components/InventorySheetsChart'
import InventoryByMonthChart from '@/components/InventoryByMonthChart'

export default {
  name: 'OrderAnalyticsPage',
  components: {
    CircleLoader,
    OrdersByMonthChart,
    OrdersByQuarterChart,
    OrdersByTypeChart,
    OrdersByStatusChart,
    UpcomingOrdersByStageChart,
    OrdersBySalespersonChart,
    InventoryTubesChart,
    InventorySheetsChart,
    InventoryByMonthChart,
  },
  inject: {
    userStore,
  },
  data: function () {
    return {
      loading: true,
      analytics: null,
      showTypeValue: false,
      showMonthValue: false,
      showQuarterValue: false,
      showSalesValue: false,
      inventoryTrendType: 'tubes',
    }
  },
  computed: {
    yearOverYearTrend() {
      if (!this.analytics) {
        return 0
      }
      const thisYear = this.analytics.yearOverYear.thisYear.count
      const lastYear = this.analytics.yearOverYear.lastYear.count
      if (lastYear === 0) {
        return 0
      }
      return Math.round(((thisYear - lastYear) / lastYear) * 100)
    },
    avgOrderValueTotal() {
      if (!this.analytics || !this.analytics.avgOrderValue.length) {
        return 0
      }
      const totalValue = _.sumBy(this.analytics.avgOrderValue, 'totalValue')
      const totalCount = _.sumBy(this.analytics.avgOrderValue, 'count')
      if (totalCount === 0) {
        return 0
      }
      return Math.round(totalValue / totalCount)
    },
    totalOrdersWithValue() {
      if (!this.analytics || !this.analytics.avgOrderValue.length) {
        return 0
      }
      return _.sumBy(this.analytics.avgOrderValue, 'count')
    },
    manufacturingOrderCount() {
      if (!this.analytics || !this.analytics.manufacturingPipeline.length) {
        return 0
      }
      return _.sumBy(this.analytics.manufacturingPipeline, 'count')
    },
    manufacturingOrderValue() {
      if (!this.analytics || !this.analytics.manufacturingPipeline.length) {
        return 0
      }
      return _.sumBy(this.analytics.manufacturingPipeline, 'totalValue')
    },
    upcomingInventory() {
      return this.analytics?.inventory?.upcoming?.summary || null
    },
    historicalInventory() {
      return this.analytics?.inventory?.historical?.summary || null
    },
    upcomingOrderCount() {
      return this.analytics?.inventory?.upcoming?.orderCount || 0
    },
    historicalOrderCount() {
      return this.analytics?.inventory?.historical?.orderCount || 0
    },
  },
  methods: {
    formatCurrency(value) {
      if (!value) {
        return '0'
      }
      return Math.round(value).toLocaleString()
    },
    formatNumber(value) {
      if (!value) {
        return '0'
      }
      return Math.round(value).toLocaleString()
    },
    getAnalytics() {
      this.loading = true
      return axios
        .get('/services/orders/analytics')
        .then((response) => {
          this.analytics = response.data
          console.log('Analytics data:', this.analytics)
        })
        .catch((err) => {
          console.log('Error fetching analytics:', err)
          this.$toasted.error('Failed to load analytics data')
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
  mounted() {
    this.getAnalytics()
  },
}
</script>

<style lang="scss" scoped>
.order-analytics-page {
  padding: 20px;
}

.summary-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;

  h5 {
    font-size: 14px;
    color: #666;
    margin-bottom: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .big-number {
    font-size: 36px;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 5px;
  }

  .sub-text {
    font-size: 16px;
    color: #666;
    margin-bottom: 5px;
  }

  .sub-text-small {
    font-size: 12px;
    color: #999;
    margin-top: 5px;
  }

  .trend {
    font-size: 14px;
    font-weight: 600;
    margin-top: 10px;

    &.positive {
      color: #5cb85c;
    }

    &.negative {
      color: #d9534f;
    }
  }
}

.chart-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;

  h4 {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 20px;
  }
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h4 {
    margin-bottom: 0;
  }
}

.toggle-group {
  display: flex;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;

  button {
    padding: 6px 16px;
    border: none;
    background: white;
    color: #666;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;

    &:not(:last-child) {
      border-right: 1px solid #ddd;
    }

    &.active {
      background: #3c58e7;
      color: white;
    }

    &:hover:not(.active) {
      background: #f5f5f5;
    }
  }
}

.section-header {
  margin-top: 40px;
  margin-bottom: 20px;

  h3 {
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 5px;
  }

  p {
    color: #666;
    margin-bottom: 0;
  }
}

.pipeline-list {
  margin-top: 20px;
}

.pipeline-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  .stage-name {
    font-weight: 600;
    color: #2c3e50;
    flex: 1;
  }

  .stage-count {
    color: #666;
    margin-right: 20px;
  }

  .stage-value {
    font-weight: 600;
    color: #3c58e7;
  }
}

.table {
  margin-top: 20px;

  th {
    font-weight: 600;
    color: #2c3e50;
    border-top: none;
  }

  td {
    vertical-align: middle;
  }
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background: #f0f0f0;
  color: #666;
  border-radius: 12px;
  margin-left: 10px;
}

.inventory-details {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-top: 20px;

  h5 {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
    color: #2c3e50;
  }
}

.detail-item {
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }

  strong {
    color: #2c3e50;
  }
}
</style>

